import type {
  EvaluationLevelDisplayLabels,
  EvaluationLevelStepCount,
  EvaluationPlanAttachmentSlot,
  EvaluationPlanLevelFields,
} from './evaluationPlan';
import { isAutoDocTitle } from '../utils/evaluationPlanDocTitle';
import {
  formatAchievementStandardLabel,
  clampLevelStepCount,
  EVALUATION_LEVEL_LABELS,
  getActiveLevelKeys,
  getActiveLevelLabels,
} from './evaluationPlan';

export const EVALUATION_PLAN_WEB_DOCUMENT_VERSION = 1 as const;

export type EvaluationPlanWebDocumentColumn = {
  id: string;
  header: string;
  isUserAdded?: boolean;
  /** 기본 '평가 기준' 열 표시 (제목 편집 가능해져 헤더 텍스트로 식별할 수 없으므로) */
  isCriterion?: boolean;
  /** 기본 '구분'(행 라벨) 열 표시 — 제목 편집 가능, 텍스트로 식별하지 않음 */
  isGubun?: boolean;
};

/** 행 라벨 열 기본 제목: '채점'·'기준' 두 줄 */
/** 첫 데이터 열(기준 열) 기본 제목 — 예전 기본 '평가 기준' 은 표시 시 이것으로 */
export const DEFAULT_CRITERION_COLUMN_HEADER = '평가 항목';
const LEGACY_CRITERION_HEADERS = new Set(['평가 기준', '평가기준']);
const LEGACY_ADDED_COLUMN_HEADER = '열 제목';
const AUTO_ITEM_HEADER_RE = /^평가 항목(\d*)$/;

/** + 로 추가하는 열 기본 제목: '평가 항목N' (기존 번호 최댓값·열 수 중 큰 값 + 1 → 삭제 후에도 중복 없음) */
export function nextWebDocumentItemColumnHeader(columns: EvaluationPlanWebDocumentColumn[]): string {
  let maxN = 1;
  let itemCount = 0;
  columns.forEach((col) => {
    if (col.isCriterion || col.isUserAdded) itemCount += 1;
    const m = AUTO_ITEM_HEADER_RE.exec(col.header.trim());
    if (m) maxN = Math.max(maxN, m[1] ? Number(m[1]) : 1);
  });
  return `평가 항목${Math.max(maxN, itemCount) + 1}`;
}

export const DEFAULT_NOTICE_LABEL_LINES = ['평가 시', '유의사항'];

/** '평가 시 유의사항'(dual_label) 행 라벨 줄 — 비었거나 없으면 기본값 */
export function getWebDocumentDualLabelLines(row: { labelLines?: string[] }): string[] {
  const lines = row.labelLines ?? [];
  return lines.some((l) => l.trim()) ? lines : DEFAULT_NOTICE_LABEL_LINES;
}

/** 행 라벨 열 기본 제목 (한 줄) */
export const DEFAULT_GUBUN_COLUMN_HEADER = '채점 기준';
/** 예전 기본 제목들 — 저장값이 이것이면 새 기본값으로 표시(사용자 지정 제목 아님) */
export const LEGACY_GUBUN_COLUMN_HEADER = '구분';
const LEGACY_GUBUN_HEADERS = new Set(['구분', '채점\n기준']);

/** '구분'(행 라벨) 열 여부 — 마커 우선, 마커 없는 예전 문서는 기본 열 + 제목 '구분' */
export function isWebDocumentGubunColumn(col: {
  header: string;
  isUserAdded?: boolean;
  isCriterion?: boolean;
  isGubun?: boolean;
}): boolean {
  if (col.isGubun) return true;
  return (
    !col.isUserAdded &&
    !col.isCriterion &&
    (LEGACY_GUBUN_HEADERS.has(col.header) || col.header === DEFAULT_GUBUN_COLUMN_HEADER)
  );
}

export function webDocumentColumnDisplayHeader(col: { header: string; isCriterion?: boolean; isGubun?: boolean }): string {
  if (col.header.trim()) return col.header;
  if (col.isGubun) return DEFAULT_GUBUN_COLUMN_HEADER;
  if (col.isCriterion) return DEFAULT_CRITERION_COLUMN_HEADER;
  return col.header;
}

export type EvaluationPlanWebDocumentRow = {
  id: string;
  label: string;
  memo?: string;
  cells: Record<string, string>;
  isUserAdded?: boolean;
};

export type EvaluationPlanWebDocumentMetaRow = {
  id: string;
  label: string;
  value: string;
  /** single: 라벨+값, pair: 2열 라벨·값, dual_label: 라벨 2줄+값(또는 pairValue) */
  layout?: 'single' | 'pair' | 'dual_label';
  labelLines?: string[];
  pairLabel?: string;
  pairValue?: string;
  readOnly?: boolean;
  isUserAdded?: boolean;
  /** 기본 행 숨김(삭제) — 편집기에서 다시 추가 가능. 보기·인쇄·엑셀에서 제외 */
  hidden?: boolean;
};

export type EvaluationPlanWebDocumentAddMarker = {
  id: string;
  kind: 'row' | 'column';
  afterId: string;
};

export type EvaluationPlanWebDocumentExampleAnswer = {
  example: string;
  answer: string;
  /** 섹션 제목(편집 가능). 없거나 비면 '평가 과제' */
  title?: string;
};

export const DEFAULT_EXAMPLE_ANSWER_TITLE = '평가 과제';

export function getExampleAnswerTitle(data: { exampleAnswer?: EvaluationPlanWebDocumentExampleAnswer | null }): string {
  const t = data.exampleAnswer?.title;
  return typeof t === 'string' && t.trim() ? t : DEFAULT_EXAMPLE_ANSWER_TITLE;
}

export type EvaluationPlanWebDocumentData = {
  version: typeof EVALUATION_PLAN_WEB_DOCUMENT_VERSION;
  slot: EvaluationPlanAttachmentSlot;
  title: string;
  metaRows: EvaluationPlanWebDocumentMetaRow[];
  metaRowAddAfterIds: string[];
  exampleAnswer: EvaluationPlanWebDocumentExampleAnswer;
  columns: EvaluationPlanWebDocumentColumn[];
  columnAddAfterIds: string[];
  rows: EvaluationPlanWebDocumentRow[];
  rowAddAfterIds: string[];
};

function newId(): string {
  return crypto.randomUUID();
}

function parseExampleAnswer(raw: unknown): EvaluationPlanWebDocumentExampleAnswer {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return { example: '', answer: '' };
  }
  const obj = raw as Partial<EvaluationPlanWebDocumentExampleAnswer>;
  return {
    example: typeof obj.example === 'string' ? obj.example : '',
    answer: typeof obj.answer === 'string' ? obj.answer : '',
    ...(typeof obj.title === 'string' ? { title: obj.title } : {}),
  };
}

export function parseEvaluationPlanWebDocumentData(raw: unknown): EvaluationPlanWebDocumentData | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const obj = raw as Partial<EvaluationPlanWebDocumentData>;
  if (obj.version !== EVALUATION_PLAN_WEB_DOCUMENT_VERSION) return null;
  if (obj.slot !== 'criterion' && obj.slot !== 'sheet') return null;
  if (typeof obj.title !== 'string') return null;
  if (!Array.isArray(obj.metaRows) || !Array.isArray(obj.columns) || !Array.isArray(obj.rows)) return null;

  const columns = obj.columns
    .filter((col): col is EvaluationPlanWebDocumentColumn => !!col && typeof col.id === 'string')
    .map((col) => ({
      id: col.id,
      header: typeof col.header === 'string' ? col.header : '',
      isUserAdded: col.isUserAdded === true,
      ...(col.isCriterion === true ? { isCriterion: true } : {}),
      ...(col.isGubun === true ? { isGubun: true } : {}),
    }));
  if (!columns.length) return null;

  const rows = obj.rows
    .filter((row): row is EvaluationPlanWebDocumentRow => !!row && typeof row.id === 'string')
    .map((row) => ({
      id: row.id,
      label: typeof row.label === 'string' ? row.label : '',
      memo: typeof row.memo === 'string' ? row.memo : '',
      cells: row.cells && typeof row.cells === 'object' ? { ...row.cells } : {},
      isUserAdded: row.isUserAdded === true,
    }));

  const metaRows: EvaluationPlanWebDocumentMetaRow[] = obj.metaRows
    .filter((row): row is EvaluationPlanWebDocumentMetaRow => !!row && typeof row.id === 'string')
    .map((row) => ({
      id: row.id,
      label: typeof row.label === 'string' ? row.label : '',
      value: typeof row.value === 'string' ? row.value : '',
      layout:
        row.layout === 'pair'
          ? ('pair' as const)
          : row.layout === 'dual_label'
            ? ('dual_label' as const)
            : ('single' as const),
      labelLines: Array.isArray(row.labelLines)
        ? row.labelLines.filter((line): line is string => typeof line === 'string')
        : undefined,
      pairLabel: typeof row.pairLabel === 'string' ? row.pairLabel : undefined,
      pairValue: typeof row.pairValue === 'string' ? row.pairValue : undefined,
      readOnly: row.readOnly === true,
      isUserAdded: row.isUserAdded === true,
      ...(row.hidden === true ? { hidden: true } : {}),
    }));

  const normalized = {
    version: EVALUATION_PLAN_WEB_DOCUMENT_VERSION,
    slot: obj.slot,
    title: obj.title,
    metaRows,
    metaRowAddAfterIds: Array.isArray(obj.metaRowAddAfterIds)
      ? obj.metaRowAddAfterIds.filter((id): id is string => typeof id === 'string')
      : [],
    exampleAnswer: parseExampleAnswer(obj.exampleAnswer),
    columns,
    columnAddAfterIds: Array.isArray(obj.columnAddAfterIds)
      ? obj.columnAddAfterIds.filter((id): id is string => typeof id === 'string')
      : [],
    rows,
    rowAddAfterIds: Array.isArray(obj.rowAddAfterIds)
      ? obj.rowAddAfterIds.filter((id): id is string => typeof id === 'string')
      : [],
  };

  return normalizeWebDocumentLabels(
    stripWebDocumentSchoolYearMetaRows(
      normalizeLegacyWebDocumentMetaRows(
        normalizeLegacyWebDocumentAddMarkers(
          normalizeDualLabelMetaValues(normalizeLegacyWebDocumentColumns(normalized))
        )
      )
    )
  );
}

export function getWebDocumentDisplayAcademicYear(): number {
  return new Date().getFullYear();
}

export function formatWebDocumentAcademicYearLabel(year = getWebDocumentDisplayAcademicYear()): string {
  return `${year}학년도`;
}

export function isWebDocumentSchoolYearMetaRow(row: EvaluationPlanWebDocumentMetaRow): boolean {
  if (row.isUserAdded || row.layout === 'pair' || row.layout === 'dual_label') return false;
  if (row.label.trim()) return false;
  const value = row.value.trim();
  return !value || value === '0000학년도' || /^\d{4}학년도$/.test(value);
}

export function stripWebDocumentSchoolYearMetaRows(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentData {
  const removedIds = new Set(
    data.metaRows.filter(isWebDocumentSchoolYearMetaRow).map((row) => row.id)
  );
  if (!removedIds.size) return data;

  return {
    ...data,
    metaRows: data.metaRows.filter((row) => !removedIds.has(row.id)),
    metaRowAddAfterIds: data.metaRowAddAfterIds.filter((id) => !removedIds.has(id)),
  };
}

function normalizeWebDocumentLabels(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentData {
  const metaRows = data.metaRows.map((row) => {
    if (row.isUserAdded) return row;

    let { label, pairLabel } = row;
    if (label === '평가요소') label = '평가 요소';
    if (label === '영역') label = '평가 영역';
    if (pairLabel === '평가시기') pairLabel = '평가 시기';
    if (pairLabel === '평가유형' || pairLabel === '평가 유형') pairLabel = '평가 방법';

    if (label === row.label && pairLabel === row.pairLabel) return row;
    return { ...row, label, pairLabel };
  });

  /** 기존 문서(isCriterion 없음): 첫 기본 '평가 기준' 열을 기준 열로 표시 → 이후 제목을 바꿔도 식별 유지 */
  const hasCriterionFlag = data.columns.some((col) => col.isCriterion);
  let criterionMarked = hasCriterionFlag;
  let gubunMarked = data.columns.some((col) => col.isGubun);
  const columns = data.columns.map((col) => {
    let next = col;
    if (!gubunMarked && isWebDocumentGubunColumn(col)) {
      gubunMarked = true;
      next = { ...next, isGubun: true };
    }
    /** 예전 기본 제목('구분', 두 줄 '채점\n기준', 빈값) → '채점 기준' (사용자 지정 제목은 유지) */
    if (next.isGubun && (LEGACY_GUBUN_HEADERS.has(next.header) || !next.header.trim())) {
      next = { ...next, header: DEFAULT_GUBUN_COLUMN_HEADER };
    }
    if (!col.isUserAdded && col.header === '평가기준') next = { ...next, header: '평가 기준' };
    if (!criterionMarked && !next.isUserAdded && next.header === '평가 기준') {
      criterionMarked = true;
      next = { ...next, isCriterion: true };
    }
    return next;
  });
  /** 예전 기본 제목 → 새 기본값: 기준 열 '평가 기준'/빈값 → '평가 항목', 추가 열 '열 제목'/빈값 → 위치 기준 '평가 항목N' */
  let itemPos = 0;
  for (let i = 0; i < columns.length; i += 1) {
    const col = columns[i];
    if (!col.isCriterion && !col.isUserAdded) continue;
    itemPos += 1;
    const h = col.header.trim();
    if (col.isCriterion && (!h || LEGACY_CRITERION_HEADERS.has(h))) {
      columns[i] = { ...col, header: DEFAULT_CRITERION_COLUMN_HEADER };
    } else if (col.isUserAdded && (!h || h === LEGACY_ADDED_COLUMN_HEADER)) {
      columns[i] = { ...col, header: `평가 항목${itemPos}` };
    }
  }

  return { ...data, metaRows, columns };
}

function normalizeDualLabelMetaValues(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentData {
  const metaRows = data.metaRows.map((row) => {
    if (row.layout !== 'dual_label') return row;
    if (!row.pairValue?.trim()) return row;
    const mergedValue = [row.value, row.pairValue].filter((part) => part.trim()).join('\n');
    return { ...row, value: mergedValue, pairValue: undefined };
  });
  return { ...data, metaRows };
}

function normalizeLegacyWebDocumentAddMarkers(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentData {
  const elementRow = data.metaRows.find(
    (row) => (row.label.trim() === '평가 요소' || row.label.trim() === '평가요소') && !row.isUserAdded
  );
  if (!elementRow) return data;

  return {
    ...data,
    metaRowAddAfterIds: [elementRow.id],
  };
}

const LEGACY_REMOVED_COLUMN_HEADERS = new Set(['예시', '답안']);

function normalizeLegacyWebDocumentColumns(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentData {
  const removedIds = new Set(
    data.columns.filter((col) => !col.isUserAdded && LEGACY_REMOVED_COLUMN_HEADERS.has(col.header)).map((col) => col.id)
  );
  if (!removedIds.size && data.rowAddAfterIds.length === 0) return data;

  const columns = data.columns.filter((col) => !removedIds.has(col.id));
  const rows = data.rows.map((row) => {
    const cells = { ...row.cells };
    removedIds.forEach((id) => {
      delete cells[id];
    });
    return { ...row, cells };
  });

  return {
    ...data,
    columns,
    rows,
    columnAddAfterIds: data.columnAddAfterIds.filter((id) => !removedIds.has(id)),
    rowAddAfterIds: [],
  };
}

function normalizeLegacyWebDocumentMetaRows(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentData {
  const timingIdx = data.metaRows.findIndex((row) => row.label.trim() === '평가 시');
  const noticeIdx = data.metaRows.findIndex((row) => row.label.trim() === '유의사항');
  if (timingIdx < 0 || noticeIdx < 0) return data;

  const timingRow = data.metaRows[timingIdx];
  const noticeRow = data.metaRows[noticeIdx];
  const mergedRow: EvaluationPlanWebDocumentMetaRow = {
    id: timingRow.id,
    label: '',
    labelLines: ['평가 시', '유의사항'],
    value: [timingRow.value, noticeRow.value].filter(Boolean).join('\n'),
    layout: 'dual_label',
  };

  const metaRows = data.metaRows.filter((row) => row.id !== noticeRow.id);
  const mergedIndex = metaRows.findIndex((row) => row.id === timingRow.id);
  metaRows[mergedIndex] = mergedRow;

  return {
    ...data,
    metaRows,
    metaRowAddAfterIds: data.metaRowAddAfterIds.map((id) =>
      id === noticeRow.id ? mergedRow.id : id
    ),
  };
}

export function cloneEvaluationPlanWebDocumentData(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentData {
  return {
    ...data,
    metaRows: data.metaRows.map((row) => ({ ...row })),
    metaRowAddAfterIds: [...data.metaRowAddAfterIds],
    exampleAnswer: { ...data.exampleAnswer },
    columns: data.columns.map((col) => ({ ...col })),
    columnAddAfterIds: [...data.columnAddAfterIds],
    rows: data.rows.map((row) => ({ ...row, cells: { ...row.cells } })),
    rowAddAfterIds: [...data.rowAddAfterIds],
  };
}

export const WEB_DOCUMENT_DEFAULT_TEMPLATE_ID = '__default__';

export type EvaluationPlanWebDocumentTemplateStructure = Pick<
  EvaluationPlanWebDocumentData,
  'title' | 'metaRows' | 'metaRowAddAfterIds' | 'columns' | 'columnAddAfterIds' | 'rows' | 'rowAddAfterIds'
> & {
  /** '평가 과제' 제목 바 텍스트 (양식에 포함; 예전 양식엔 없음 → 기본값) */
  exampleAnswerTitle?: string;
};

function remapIdList(ids: string[], idMap: Map<string, string>): string[] {
  return ids.map((id) => idMap.get(id) ?? id);
}

/** 흰색 입력칸 데이터를 제외한 양식 구조만 추출합니다. */
export function extractWebDocumentTemplateStructure(
  data: EvaluationPlanWebDocumentData
): EvaluationPlanWebDocumentTemplateStructure {
  const metaRows = data.metaRows
    .filter((row) => !isWebDocumentSchoolYearMetaRow(row))
    .map((row) => {
      const next = { ...row, pairValue: undefined };
      return { ...next, value: '' };
    });

  const rows = data.rows.map((row) => ({
    ...row,
    memo: row.memo ?? '',
    cells: Object.fromEntries(Object.keys(row.cells).map((columnId) => [columnId, ''])),
  }));

  return {
    title: data.title,
    exampleAnswerTitle: data.exampleAnswer?.title ?? '',
    metaRows,
    metaRowAddAfterIds: [...data.metaRowAddAfterIds],
    columns: data.columns.map((column) => ({ ...column })),
    columnAddAfterIds: [...data.columnAddAfterIds],
    rows,
    rowAddAfterIds: [...data.rowAddAfterIds],
  };
}

type WebDocumentTemplateStructureComparable = {
  title: string;
  exampleAnswerTitle: string;
  metaRows: Array<{
    label: string;
    layout: 'single' | 'pair' | 'dual_label';
    labelLines: string[] | null;
    pairLabel: string | null;
    readOnly: boolean;
    isUserAdded: boolean;
    hidden: boolean;
  }>;
  metaRowAddAfterIndices: number[];
  columns: Array<{ header: string; isUserAdded: boolean; isGubun: boolean; isCriterion: boolean }>;
  columnAddAfterIndices: number[];
  rows: Array<{ label: string; memo: string; isUserAdded: boolean }>;
  rowAddAfterIndices: number[];
};

function normalizeWebDocumentTemplateStructureForCompare(
  structure: EvaluationPlanWebDocumentTemplateStructure
): WebDocumentTemplateStructureComparable {
  const metaRowIndex = new Map(structure.metaRows.map((row, index) => [row.id, index]));
  const columnIndex = new Map(structure.columns.map((column, index) => [column.id, index]));
  const rowIndex = new Map(structure.rows.map((row, index) => [row.id, index]));

  return {
    // 과목 자동 제목(예: '국어 수행평가 기준안')은 기본 제목과 같게 비교
    title: isAutoDocTitle('criterion', structure.title) ? '수행평가 기준안' : structure.title,
    // 빈 값/예전 양식은 표시 기본값으로 맞춰 비교(기본값 ↔ 빈 값 차이로 오탐 방지)
    exampleAnswerTitle: getExampleAnswerTitle({
      exampleAnswer: { example: '', answer: '', title: structure.exampleAnswerTitle },
    }),
    metaRows: structure.metaRows.map((row) => ({
      label: row.label,
      layout: row.layout ?? 'single',
      labelLines: row.layout === 'dual_label' ? getWebDocumentDualLabelLines(row) : row.labelLines ?? null,
      pairLabel: row.pairLabel ?? null,
      readOnly: row.readOnly === true,
      isUserAdded: row.isUserAdded === true,
      hidden: row.hidden === true,
    })),
    metaRowAddAfterIndices: structure.metaRowAddAfterIds.map((id) => metaRowIndex.get(id) ?? -1),
    columns: structure.columns.map((column) => ({
      header: webDocumentColumnDisplayHeader(column),
      isUserAdded: column.isUserAdded === true,
      isGubun: column.isGubun === true,
      isCriterion: column.isCriterion === true,
    })),
    columnAddAfterIndices: structure.columnAddAfterIds.map((id) => columnIndex.get(id) ?? -1),
    rows: structure.rows.map((row) => ({
      label: row.label,
      memo: row.memo ?? '',
      isUserAdded: row.isUserAdded === true,
    })),
    rowAddAfterIndices: structure.rowAddAfterIds.map((id) => rowIndex.get(id) ?? -1),
  };
}

/** 양식 구조(타이틀·행·열 등)가 동일한지 비교합니다. ID는 무시합니다. */
export function webDocumentTemplateStructuresEqual(
  left: EvaluationPlanWebDocumentTemplateStructure,
  right: EvaluationPlanWebDocumentTemplateStructure
): boolean {
  return (
    JSON.stringify(normalizeWebDocumentTemplateStructureForCompare(left)) ===
    JSON.stringify(normalizeWebDocumentTemplateStructureForCompare(right))
  );
}

/** 현재 문서 구조가 기준 양식 구조와 다른지 확인합니다. */
export function webDocumentTemplateStructureDirty(
  data: EvaluationPlanWebDocumentData,
  baseline: EvaluationPlanWebDocumentTemplateStructure
): boolean {
  return !webDocumentTemplateStructuresEqual(extractWebDocumentTemplateStructure(data), baseline);
}

/** 흰색 입력칸에 사용자가 입력한 내용이 있는지 확인합니다. */
export function webDocumentHasWhiteFieldContent(data: EvaluationPlanWebDocumentData): boolean {
  if (data.exampleAnswer.example.trim() || data.exampleAnswer.answer.trim()) return true;

  for (const row of data.metaRows) {
    if (isWebDocumentSchoolYearMetaRow(row)) continue;
    if (row.value.trim()) return true;
    if (row.pairValue?.trim()) return true;
  }

  return data.rows.some((row) => Object.values(row.cells).some((value) => value.trim().length > 0));
}

/** 저장된 양식 구조를 새 ID로 편집 문서 데이터로 변환합니다. */
export function buildWebDocumentFromTemplateStructure(
  slot: EvaluationPlanAttachmentSlot,
  structure: EvaluationPlanWebDocumentTemplateStructure
): EvaluationPlanWebDocumentData {
  const columnIdMap = new Map<string, string>();
  const columns = structure.columns.map((column) => {
    const id = newId();
    columnIdMap.set(column.id, id);
    return { ...column, id };
  });

  const metaRowIdMap = new Map<string, string>();
  const metaRows = structure.metaRows
    .filter((row) => !isWebDocumentSchoolYearMetaRow(row))
    .map((row) => {
      const id = newId();
      metaRowIdMap.set(row.id, id);
      return { ...row, id, pairValue: undefined, value: '' };
    });

  const rowIdMap = new Map<string, string>();
  const dataColumns = columns.filter((column) => !isWebDocumentGubunColumn(column));
  const rows = structure.rows.map((row) => {
    const id = newId();
    rowIdMap.set(row.id, id);
    const cells = Object.fromEntries(dataColumns.map((column) => [column.id, '']));
    return { ...row, id, memo: row.memo ?? '', cells };
  });

  return {
    version: EVALUATION_PLAN_WEB_DOCUMENT_VERSION,
    slot,
    title: structure.title,
    metaRows,
    metaRowAddAfterIds: remapIdList(structure.metaRowAddAfterIds, metaRowIdMap),
    exampleAnswer: {
      example: '',
      answer: '',
      ...(structure.exampleAnswerTitle ? { title: structure.exampleAnswerTitle } : {}),
    },
    columns,
    columnAddAfterIds: remapIdList(structure.columnAddAfterIds, columnIdMap),
    rows,
    rowAddAfterIds: remapIdList(structure.rowAddAfterIds, rowIdMap),
  };
}

function createCriterionColumns(): EvaluationPlanWebDocumentColumn[] {
  const criterionId = newId();
  return [
    { id: newId(), header: DEFAULT_GUBUN_COLUMN_HEADER, isGubun: true },
    { id: criterionId, header: DEFAULT_CRITERION_COLUMN_HEADER, isCriterion: true },
  ];
}

function createLevelRows(
  columns: EvaluationPlanWebDocumentColumn[],
  levelStepCount: EvaluationLevelStepCount = 5,
  displayLabels?: Partial<EvaluationLevelDisplayLabels> | null
): EvaluationPlanWebDocumentRow[] {
  return getActiveLevelLabels(levelStepCount, displayLabels).map((label) => ({
    id: newId(),
    label,
    memo: '',
    cells: Object.fromEntries(
      columns
        .filter((col) => !isWebDocumentGubunColumn(col))
        .map((col) => [col.id, ''])
    ),
  }));
}

export function getWebDocumentCriterionColumnId(data: EvaluationPlanWebDocumentData): string | null {
  return (
    data.columns.find((col) => col.isCriterion && !col.isUserAdded)?.id ??
    data.columns.find(
      (col) => LEGACY_CRITERION_HEADERS.has(col.header) && !col.isUserAdded
    )?.id ??
    null
  );
}

export function getWebDocumentColumnInsertAfterId(data: EvaluationPlanWebDocumentData): string | null {
  const criterionId = getWebDocumentCriterionColumnId(data);
  if (!criterionId) return data.columns[data.columns.length - 1]?.id ?? null;

  const criterionIndex = data.columns.findIndex((col) => col.id === criterionId);
  for (let index = data.columns.length - 1; index > criterionIndex; index -= 1) {
    const column = data.columns[index];
    if (column.isUserAdded) return column.id;
  }
  return criterionId;
}

export function createDefaultEvaluationPlanWebDocument(
  slot: EvaluationPlanAttachmentSlot,
  levelStepCount: EvaluationLevelStepCount = 5,
  displayLabels?: Partial<EvaluationLevelDisplayLabels> | null
): EvaluationPlanWebDocumentData {
  const columns = createCriterionColumns();
  const criterionCol = columns.find((col) => col.isCriterion);
  const steps = clampLevelStepCount(levelStepCount);
  const rows = createLevelRows(columns, steps, displayLabels);

  const metaUnitId = newId();
  const metaAreaId = newId();
  const metaStandardId = newId();
  const metaElementId = newId();
  const metaTimingNoticeId = newId();

  const title = slot === 'criterion' ? '수행평가 기준안' : '평가지';

  return {
    version: EVALUATION_PLAN_WEB_DOCUMENT_VERSION,
    slot,
    title,
    metaRows: [
      {
        id: metaUnitId,
        label: '단원명',
        value: '',
        layout: 'pair',
        pairLabel: '평가 시기',
        pairValue: '',
      },
      {
        id: metaAreaId,
        label: '평가 영역',
        value: '',
        layout: 'pair',
        pairLabel: '평가 방법',
        pairValue: '',
      },
      { id: metaStandardId, label: '성취기준', value: '', layout: 'single' },
      { id: metaElementId, label: '평가 요소', value: '', layout: 'single' },
      {
        id: metaTimingNoticeId,
        label: '',
        labelLines: ['평가 시', '유의사항'],
        value: '',
        layout: 'dual_label',
      },
    ],
    metaRowAddAfterIds: [metaElementId],
    exampleAnswer: { example: '', answer: '' },
    columns,
    columnAddAfterIds: criterionCol ? [criterionCol.id] : [],
    rows,
    rowAddAfterIds: [],
  };
}

export function getWebDocumentMetaElementRowId(data: EvaluationPlanWebDocumentData): string | null {
  return (
    data.metaRows.find(
      (row) => (row.label.trim() === '평가 요소' || row.label.trim() === '평가요소') && !row.isUserAdded
    )?.id ?? null
  );
}

export function getWebDocumentMetaRowInsertAfterId(data: EvaluationPlanWebDocumentData): string | null {
  const elementId = getWebDocumentMetaElementRowId(data);
  if (!elementId) return data.metaRowAddAfterIds[0] ?? null;

  const elementIndex = data.metaRows.findIndex((row) => row.id === elementId);
  if (elementIndex < 0) return elementId;

  let insertAfterId = elementId;
  for (let index = elementIndex + 1; index < data.metaRows.length; index += 1) {
    const row = data.metaRows[index];
    if (row.isUserAdded) {
      insertAfterId = row.id;
      continue;
    }
    break;
  }
  return insertAfterId;
}

export function addWebDocumentColumnAfter(
  data: EvaluationPlanWebDocumentData,
  afterColumnId: string,
  header?: string
): EvaluationPlanWebDocumentData {
  const index = data.columns.findIndex((col) => col.id === afterColumnId);
  if (index < 0) return data;

  const newColumn: EvaluationPlanWebDocumentColumn = {
    id: newId(),
    header: header ?? nextWebDocumentItemColumnHeader(data.columns),
    isUserAdded: true,
  };

  const columns = [...data.columns.slice(0, index + 1), newColumn, ...data.columns.slice(index + 1)];
  const rows = data.rows.map((row) => ({
    ...row,
    cells: { ...row.cells, [newColumn.id]: '' },
  }));

  return {
    ...data,
    columns,
    rows,
  };
}

export function addWebDocumentRowAfter(
  data: EvaluationPlanWebDocumentData,
  afterRowId: string,
  label = '행 제목'
): EvaluationPlanWebDocumentData {
  const index = data.rows.findIndex((row) => row.id === afterRowId);
  if (index < 0) return data;

  const newRow: EvaluationPlanWebDocumentRow = {
    id: newId(),
    label,
    isUserAdded: true,
    cells: Object.fromEntries(
      data.columns.filter((col) => !isWebDocumentGubunColumn(col)).map((col) => [col.id, ''])
    ),
  };

  const rows = [...data.rows.slice(0, index + 1), newRow, ...data.rows.slice(index + 1)];

  return {
    ...data,
    rows,
    rowAddAfterIds: data.rowAddAfterIds.map((id) => (id === afterRowId ? newRow.id : id)),
  };
}

export function addWebDocumentMetaRowAfter(
  data: EvaluationPlanWebDocumentData,
  afterMetaRowId: string,
  label = '항목'
): EvaluationPlanWebDocumentData {
  const index = data.metaRows.findIndex((row) => row.id === afterMetaRowId);
  if (index < 0) return data;

  const newRow: EvaluationPlanWebDocumentMetaRow = {
    id: newId(),
    label,
    value: '',
    layout: 'single',
    isUserAdded: true,
  };

  const metaRows = [...data.metaRows.slice(0, index + 1), newRow, ...data.metaRows.slice(index + 1)];

  return {
    ...data,
    metaRows,
  };
}

export function metaRowHasContent(row: EvaluationPlanWebDocumentMetaRow): boolean {
  if (row.label.trim()) return true;
  if (row.value.trim()) return true;
  if (row.pairLabel?.trim()) return true;
  if (row.pairValue?.trim()) return true;
  return false;
}

export function rubricRowHasContent(
  data: EvaluationPlanWebDocumentData,
  row: EvaluationPlanWebDocumentRow
): boolean {
  if (row.memo?.trim()) return true;
  if (row.label.trim() && row.isUserAdded) return true;
  return Object.values(row.cells).some((value) => value.trim().length > 0);
}

export function columnHasContent(
  data: EvaluationPlanWebDocumentData,
  columnId: string
): boolean {
  const column = data.columns.find((col) => col.id === columnId);
  if (!column) return false;
  if (
    column.isUserAdded &&
    column.header.trim() &&
    column.header !== LEGACY_ADDED_COLUMN_HEADER &&
    !AUTO_ITEM_HEADER_RE.test(column.header.trim())
  )
    return true;
  return data.rows.some((row) => (row.cells[columnId] ?? '').trim().length > 0);
}

export function removeWebDocumentColumn(
  data: EvaluationPlanWebDocumentData,
  columnId: string
): EvaluationPlanWebDocumentData {
  const column = data.columns.find((col) => col.id === columnId);
  if (!column?.isUserAdded) return data;

  const removedIndex = data.columns.findIndex((col) => col.id === columnId);
  const previousColumn = removedIndex > 0 ? data.columns[removedIndex - 1] : null;

  return {
    ...data,
    columns: data.columns.filter((col) => col.id !== columnId),
    columnAddAfterIds: data.columnAddAfterIds.map((id) =>
      id === columnId ? (previousColumn?.id ?? id) : id
    ),
    rows: data.rows.map((row) => {
      const { [columnId]: _removed, ...cells } = row.cells;
      return { ...row, cells };
    }),
  };
}

export function removeWebDocumentRow(
  data: EvaluationPlanWebDocumentData,
  rowId: string
): EvaluationPlanWebDocumentData {
  const row = data.rows.find((item) => item.id === rowId);
  if (!row?.isUserAdded) return data;

  const removedIndex = data.rows.findIndex((item) => item.id === rowId);
  const previousRow = removedIndex > 0 ? data.rows[removedIndex - 1] : null;

  return {
    ...data,
    rows: data.rows.filter((item) => item.id !== rowId),
    rowAddAfterIds: data.rowAddAfterIds.map((id) => (id === rowId ? (previousRow?.id ?? id) : id)),
  };
}

export function removeWebDocumentMetaRow(
  data: EvaluationPlanWebDocumentData,
  rowId: string
): EvaluationPlanWebDocumentData {
  const row = data.metaRows.find((item) => item.id === rowId);
  if (!row?.isUserAdded) return data;

  return {
    ...data,
    metaRows: data.metaRows.filter((item) => item.id !== rowId),
  };
}

export function estimateWebDocumentJsonBytes(data: EvaluationPlanWebDocumentData): number {
  return new TextEncoder().encode(JSON.stringify(data)).length;
}

export type WebDocumentPlanApplySource = {
  unit_text: string;
  evaluation_element_text: string;
  evaluation_method_text: string;
  standard_ids: string[];
} & EvaluationPlanLevelFields;

export type WebDocumentPlanApplyStandard = {
  id: string;
  area_text: string;
  achievement_code: string;
  achievement_text: string;
};

function ensureWebDocumentLevelRows(
  data: EvaluationPlanWebDocumentData,
  levelLabels: string[]
): EvaluationPlanWebDocumentData {
  const dataColumns = data.columns.filter((column) => !isWebDocumentGubunColumn(column));
  let rows = [...data.rows];

  for (let index = 0; index < levelLabels.length; index += 1) {
    const label = levelLabels[index];
    if (rows.some((row) => row.label.trim() === label)) continue;

    const newRow: EvaluationPlanWebDocumentRow = {
      id: newId(),
      label,
      memo: '',
      cells: Object.fromEntries(dataColumns.map((column) => [column.id, ''])),
    };

    if (index === 0) {
      rows.push(newRow);
      continue;
    }

    const previousLabel = levelLabels[index - 1];
    const previousIndex = rows.findIndex((row) => row.label.trim() === previousLabel);
    const insertAt = previousIndex >= 0 ? previousIndex + 1 : rows.length;
    rows.splice(insertAt, 0, newRow);
  }

  return { ...data, rows };
}

/** A~E 등 기본 기호 행 라벨을 카테고리 대체 텍스트로 맞춥니다. */
export function syncWebDocumentLevelRowLabels(
  data: EvaluationPlanWebDocumentData,
  levelStepCount: EvaluationLevelStepCount,
  displayLabels?: Partial<EvaluationLevelDisplayLabels> | null
): EvaluationPlanWebDocumentData {
  const stepCount = clampLevelStepCount(levelStepCount);
  const targetLabels = getActiveLevelLabels(stepCount, displayLabels);
  const defaultLetters = EVALUATION_LEVEL_LABELS.slice(0, stepCount);
  const rows = data.rows.map((row) => ({ ...row }));
  const usedIds = new Set<string>();

  for (let index = 0; index < targetLabels.length; index += 1) {
    const target = targetLabels[index];
    const defaultLetter = defaultLetters[index];

    let matchIdx = rows.findIndex(
      (row) => !row.isUserAdded && !usedIds.has(row.id) && row.label.trim() === target
    );
    if (matchIdx < 0) {
      matchIdx = rows.findIndex(
        (row) => !row.isUserAdded && !usedIds.has(row.id) && row.label.trim() === defaultLetter
      );
    }
    if (matchIdx < 0) {
      const levelLike = rows
        .map((row, rowIdx) => ({ row, rowIdx }))
        .filter(
          ({ row }) =>
            !row.isUserAdded &&
            !usedIds.has(row.id) &&
            (defaultLetters.includes(row.label.trim() as (typeof defaultLetters)[number]) ||
              targetLabels.includes(row.label.trim()))
        );
      if (levelLike[index]) matchIdx = levelLike[index].rowIdx;
    }

    if (matchIdx >= 0) {
      usedIds.add(rows[matchIdx].id);
      if (rows[matchIdx].label.trim() !== target) {
        rows[matchIdx] = { ...rows[matchIdx], label: target };
      }
    }
  }

  return ensureWebDocumentLevelRows({ ...data, rows }, targetLabels);
}

/** 평가계획 데이터를 기준안 웹 문서 흰색 입력칸에 반영합니다. */
export function applyEvaluationPlanToWebDocument(
  data: EvaluationPlanWebDocumentData,
  plan: WebDocumentPlanApplySource,
  standards: WebDocumentPlanApplyStandard[],
  levelStepCount: EvaluationLevelStepCount,
  displayLabels?: Partial<EvaluationLevelDisplayLabels> | null
): EvaluationPlanWebDocumentData {
  const selectedStandards = standards.filter((standard) => (plan.standard_ids ?? []).includes(standard.id));
  const areaText = selectedStandards
    .map((standard) => (standard.area_text ?? '').trim())
    .filter(Boolean)
    .join('\n');
  const achievementText = selectedStandards
    .map((standard) => formatAchievementStandardLabel(standard).trim())
    .filter(Boolean)
    .join('\n');

  let next: EvaluationPlanWebDocumentData = {
    ...data,
    metaRows: data.metaRows.map((row) => {
      if (row.isUserAdded) return row;

      const label = row.label.trim();
      if (label === '단원명') {
        return { ...row, value: plan.unit_text ?? '' };
      }
      if (label === '평가 영역' || label === '영역') {
        return {
          ...row,
          value: areaText,
          pairValue: plan.evaluation_method_text ?? '',
        };
      }
      if (label === '성취기준') {
        return { ...row, value: achievementText };
      }
      if (label === '평가 요소' || label === '평가요소') {
        return { ...row, value: plan.evaluation_element_text ?? '' };
      }
      return row;
    }),
  };

  const levelKeys = getActiveLevelKeys(levelStepCount);
  const levelLabels = getActiveLevelLabels(levelStepCount, displayLabels);
  next = syncWebDocumentLevelRowLabels(next, levelStepCount, displayLabels);

  const criterionColumnId = getWebDocumentCriterionColumnId(next);
  if (!criterionColumnId) return next;

  return {
    ...next,
    rows: next.rows.map((row) => {
      const levelIndex = levelLabels.indexOf(row.label.trim());
      if (levelIndex < 0) return row;

      const levelKey = levelKeys[levelIndex];
      return {
        ...row,
        cells: {
          ...row.cells,
          [criterionColumnId]: plan[levelKey] ?? '',
        },
      };
    }),
  };
}

/** 평가계획 데이터 반영 시 덮어쓰는 흰색 입력칸에 내용이 있는지 확인합니다. */
export function webDocumentPlanApplyTargetHasContent(
  data: EvaluationPlanWebDocumentData,
  levelStepCount: EvaluationLevelStepCount = 5,
  displayLabels?: Partial<EvaluationLevelDisplayLabels> | null
): boolean {
  for (const row of data.metaRows) {
    if (row.isUserAdded || isWebDocumentSchoolYearMetaRow(row)) continue;

    const label = row.label.trim();
    if (label === '단원명' && row.value.trim()) return true;
    if ((label === '평가 영역' || label === '영역') && (row.value.trim() || row.pairValue?.trim())) return true;
    if (label === '성취기준' && row.value.trim()) return true;
    if ((label === '평가 요소' || label === '평가요소') && row.value.trim()) return true;
  }

  const stepCount = clampLevelStepCount(levelStepCount);
  const targetLabels = getActiveLevelLabels(stepCount, displayLabels);
  const defaultLetters = EVALUATION_LEVEL_LABELS.slice(0, stepCount);
  const levelLabels = new Set([...targetLabels, ...defaultLetters]);
  const criterionColumnId = getWebDocumentCriterionColumnId(data);
  if (!criterionColumnId) return false;

  return data.rows.some((row) => {
    if (!levelLabels.has(row.label.trim())) return false;
    return (row.cells[criterionColumnId] ?? '').trim().length > 0;
  });
}
